import clsx from 'clsx'
import PropTypes from 'prop-types'
import { segmentVoucherCopied, segmentVoucherSection } from 'lib/segment'
import { copyToClipboard, throttleCallback } from 'lib/utils/common/commonUtils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { hasLocizeTranslation } from 'lib/utils/locize'
import Icon from 'components/common/icon'
import sameVoucherCode from 'components/cart/voucher-code'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import SnackBar from 'components/common/snack-bar'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import {
  getIsCoachMarkClosed,
  saveClosedCoachMarkToStorage,
} from 'pages/product/utils'
import { VOUCHER_CHECKOUT_LOCIZE } from 'components/pdp/carousel-images/voucher/voucher-checkout/const'
import { setElementsWidth } from 'components/pdp/carousel-images/voucher/utils'
import styles from './style.scss'
import VoucherList from './voucher-list/VoucherList'

const VOUCHER_COACH_MARK = 'pdp_voucher_coach_mark'

export const VoucherListModal = ({
  isLoading,
  isPhone,
  isCheckout,
  productId,
  appliedVoucher,
  voucherList,
  t,
  onVoucherSelected,
  onClose,
  isApplying,
  pendingVoucher,
  applicationError,
  listError,
  onRetry,
  language,
  onCodeEntered,
}) => {
  const thai = language?.split('-')[0] === 'th'
  const pendingLabel = hasLocizeTranslation(
    t,
    'VOUCHER_APPLYING',
    thai ? 'กำลังใช้คูปอง…' : 'Applying…',
  )
  const feedbackMessage =
    applicationError?.message?.message || applicationError?.message
  const fallbackMessage =
    applicationError?.status === 'busy'
      ? hasLocizeTranslation(
          t,
          'VOUCHER_BAG_BUSY',
          thai
            ? 'กำลังอัปเดตถุงช้อปปิ้ง กรุณาลองอีกครั้ง'
            : 'Your bag is updating. Please try again shortly.',
        )
      : hasLocizeTranslation(
          t,
          'VOUCHER_CONFIRMATION_FAILED',
          thai
            ? 'ไม่สามารถยืนยันการใช้คูปองได้ กรุณาลองอีกครั้ง'
            : 'We couldn’t confirm your voucher. Please try again.',
        )
  // states
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const manualAlreadyApplied = sameVoucherCode(
    manualCode.trim(),
    appliedVoucher,
  )
  const manualApplyLabel = manualAlreadyApplied
    ? hasLocizeTranslation(
        t,
        'CART_VOUCHER_APPLIED',
        thai ? 'ใช้แล้ว' : 'Applied',
      )
    : hasLocizeTranslation(t, 'Apply', thai ? 'ใช้' : 'Apply')
  const [isOpenCoachMark, setIsOpenCoachMark] = useState(true)
  const [voucherListItemWidth, setVoucherListItemWidth] = useState()
  const modalRef = useRef()
  const dialogRef = useRef(null)
  const closeButtonRef = useRef(null)
  const codeInputRef = useRef(null)
  const isBagEntry = Boolean(onCodeEntered)
  const voucherPieceRef = useRef(null)
  const localizeHeader = isCheckout
    ? hasLocizeTranslation(
        t,
        VOUCHER_CHECKOUT_LOCIZE.VOUCHER_CHECKOUT_HEADER.key,
        VOUCHER_CHECKOUT_LOCIZE.VOUCHER_CHECKOUT_HEADER.text,
      )
    : hasLocizeTranslation(t, 'VOUCHER_LIST_HEADER', 'New User Vouchers')

  useEffect(() => {
    if (!isBagEntry) return undefined
    const trigger = document.activeElement
    const hiddenElements = []
    const initialFocus = codeInputRef.current?.disabled
      ? closeButtonRef.current
      : codeInputRef.current
    initialFocus?.focus({ preventScroll: true })
    // The bag and voucher overlay share #modal. Hide only the background
    // branches, preserving later nested voucher-terms portals in that root.
    let branch = dialogRef.current
    while (branch && branch !== document.body) {
      const currentBranch = branch
      Array.from(branch.parentElement?.children || []).forEach((element) => {
        if (
          element !== currentBranch &&
          !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)
        ) {
          hiddenElements.push({
            element,
            ariaHidden: element.getAttribute('aria-hidden'),
            inert: element.getAttribute('inert'),
          })
          element.setAttribute('aria-hidden', 'true')
          element.setAttribute('inert', '')
        }
      })
      branch = branch.parentElement
    }
    return () => {
      hiddenElements.forEach(({ element, ariaHidden, inert }) => {
        if (ariaHidden === null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', ariaHidden)
        if (inert === null) element.removeAttribute('inert')
        else element.setAttribute('inert', inert)
      })
      if (trigger?.isConnected) trigger.focus({ preventScroll: true })
    }
  }, [isBagEntry])

  useEffect(() => {
    if (
      isBagEntry &&
      isApplying &&
      (!dialogRef.current?.contains(document.activeElement) ||
        document.activeElement?.disabled)
    ) {
      closeButtonRef.current?.focus({ preventScroll: true })
    }
  }, [isBagEntry, isApplying])

  const handleDialogKeyDown = (event) => {
    // Events from nested terms portals also bubble through the React tree.
    if (!isBagEntry || !dialogRef.current?.contains(event.target)) return
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
    } else if (event.key === 'Tab') {
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'a[href], button, input, select, textarea, summary, [tabindex]',
        ),
      ).filter(
        (element) =>
          !element.disabled &&
          element.tabIndex >= 0 &&
          !element.closest('[hidden], [inert], [aria-hidden="true"]'),
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
  }

  const isPmloVoucherAllUserFlagValue = true // 'EC-VoucherPDP-20220610'
  const isFlagForAllUserValid = isPmloVoucherAllUserFlagValue === true
  const isCoachMarkAlreadyClosed = getIsCoachMarkClosed(
    VOUCHER_COACH_MARK,
    true,
  )
  const isCoachMarkPresent =
    isFlagForAllUserValid && isOpenCoachMark && !isCoachMarkAlreadyClosed

  // events
  const handleCopyVoucherCode = (code) => {
    copyToClipboard(code)
    setIsSnackBarOpen(true)
    segmentVoucherCopied(productId, code)
  }

  const handleSaveCoachMarkClosed = () => {
    setIsOpenCoachMark(false)
    saveClosedCoachMarkToStorage(VOUCHER_COACH_MARK, true)
  }

  const handleHideContent = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose(e)
      }
    },
    [modalRef, onClose],
  )

  const handleUpdateCoachMarkWidth = useCallback(() => {
    const currentVoucherElemWidth =
      voucherPieceRef?.current?.getBoundingClientRect()?.width

    if (
      currentVoucherElemWidth &&
      currentVoucherElemWidth !== voucherListItemWidth
    ) {
      const roundUpWidth = Math.ceil(currentVoucherElemWidth)

      setVoucherListItemWidth(roundUpWidth)
      setElementsWidth(roundUpWidth)
    }
  }, [voucherListItemWidth])

  const handleScrollToCloseCoachMark = useCallback(() => {
    if (isCoachMarkPresent) {
      handleSaveCoachMarkClosed()
    }
  }, [isCoachMarkPresent])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isPhone && !isCheckout) {
      document.addEventListener('click', handleHideContent, true)

      return () => {
        document.removeEventListener('click', handleHideContent, true)
      }
    }
  }, [handleHideContent, isPhone, isCheckout])

  useEffect(() => {
    if (voucherList?.length && !isCheckout) {
      const options = { productId, voucherList, isVoucherViewed: false }
      segmentVoucherSection(options)
    }
  }, [productId, voucherList, isCheckout])

  useEffect(() => {
    // delay to wait for the first voucher to be rendered, then set the coach-mark width
    const coachMarkTimeOut = setTimeout(() => {
      handleUpdateCoachMarkWidth()
    }, 100)

    const modalElem = modalRef.current

    const setupListeners = () => {
      modalElem.addEventListener('scroll', handleScrollToCloseCoachMark)
      window.addEventListener(
        'resize',
        throttleCallback(handleUpdateCoachMarkWidth, 100),
        true,
      )
    }

    const cleanupListeners = () => {
      modalElem.removeEventListener('scroll', handleScrollToCloseCoachMark)
      window.removeEventListener('resize', handleUpdateCoachMarkWidth)
      clearTimeout(coachMarkTimeOut)
    }

    setupListeners()

    return cleanupListeners
  }, [modalRef, handleScrollToCloseCoachMark, handleUpdateCoachMarkWidth])

  return (
    // The keyboard handler applies only to the conditional semantic dialog.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="pdp__voucher-modal__container"
      ref={dialogRef}
      role={isBagEntry ? 'dialog' : undefined}
      aria-modal={isBagEntry ? true : undefined}
      aria-labelledby={isBagEntry ? 'bag-voucher-dialog-title' : undefined}
      tabIndex={isBagEntry ? -1 : undefined}
      onKeyDown={isBagEntry ? handleDialogKeyDown : undefined}
    >
      <style jsx>{styles}</style>
      <div
        className={clsx('pdp__voucher-modal__header', {
          'with-bottom-border': isFlagForAllUserValid,
        })}
      >
        {isBagEntry ? (
          <button
            type="button"
            className="pdp__voucher-modal__close-button"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label={hasLocizeTranslation(
              t,
              'CART_BACK_TO_BAG',
              thai ? 'กลับไปที่ถุงช้อปปิ้ง' : 'Back to bag',
            )}
          >
            <Icon src={ICONS.close} size={ICON_SIZE.medium} />
          </button>
        ) : (
          <Icon
            className="pdp__voucher-modal__close"
            alt="close-icon"
            src={ICONS.close}
            size={ICON_SIZE.medium}
            onClick={onClose}
          />
        )}
        <span
          id={isBagEntry ? 'bag-voucher-dialog-title' : undefined}
          className={Typo.price1}
        >
          {localizeHeader}
        </span>
        {!isFlagForAllUserValid && (
          <div className="pdp__voucher-modal__info">
            <span className={Typo.body2}>
              {hasLocizeTranslation(
                t,
                'VOUCHER_LIST_TITLE',
                'You can copy the following vouchers and paste in your shopping bag before place order',
              )}
            </span>
          </div>
        )}
      </div>
      <div
        className={clsx('pdp__voucher-modal__info', {
          'for-all-user': isFlagForAllUserValid,
          'for-checkout': isCheckout,
        })}
        data-cy="pdp-voucher-modal-info"
        ref={modalRef}
      >
        {onCodeEntered && (
          <form
            className="pdp__voucher-modal__code"
            onSubmit={(event) => {
              event.preventDefault()
              if (!isApplying && !manualAlreadyApplied && manualCode.trim())
                onCodeEntered(manualCode.trim())
            }}
          >
            <label htmlFor="bag-voucher-code">
              {hasLocizeTranslation(
                t,
                'CART_ENTER_VOUCHER_CODE',
                thai ? 'กรอกรหัสคูปอง' : 'Enter voucher code',
              )}
            </label>
            <div>
              <input
                id="bag-voucher-code"
                ref={codeInputRef}
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={manualCode}
                onChange={(event) => setManualCode(event.target.value)}
                disabled={isApplying}
              />
              <button
                type="submit"
                disabled={
                  isApplying || !manualCode.trim() || manualAlreadyApplied
                }
              >
                {isApplying ? pendingLabel : manualApplyLabel}
              </button>
            </div>
            <p>
              {hasLocizeTranslation(
                t,
                'CART_CHOOSE_VOUCHER_BELOW',
                thai ? 'หรือเลือกคูปองด้านล่าง' : 'Or choose a voucher below',
              )}
            </p>
          </form>
        )}
        {isApplying && (
          <p role="status" aria-live="polite" className={Typo.body2}>
            {pendingVoucher
              ? hasLocizeTranslation(
                  t,
                  'VOUCHER_WAIT_FOR_CONFIRMATION',
                  thai
                    ? 'กำลังยืนยันคูปองและยอดรวม…'
                    : 'Confirming your voucher and total…',
                )
              : hasLocizeTranslation(
                  t,
                  'VOUCHER_BAG_UPDATING',
                  thai ? 'กำลังอัปเดตถุงช้อปปิ้ง…' : 'Updating your bag…',
                )}
          </p>
        )}
        {applicationError && (
          <p role="alert" className={Typo.body2}>
            {typeof feedbackMessage === 'string' && feedbackMessage
              ? feedbackMessage
              : fallbackMessage}
          </p>
        )}
        {listError ? (
          <div className="pdp__voucher-modal__info-empty">
            <p role="alert" className={Typo.body2}>
              {hasLocizeTranslation(
                t,
                'VOUCHER_LIST_LOAD_FAILED',
                thai
                  ? 'ไม่สามารถโหลดคูปองได้ กรุณาลองอีกครั้ง'
                  : 'We couldn’t load vouchers. Please try again.',
              )}
            </p>
            <button
              type="button"
              onClick={onRetry}
              disabled={isApplying}
              style={{ minHeight: 44, padding: '0 16px' }}
            >
              {hasLocizeTranslation(
                t,
                'VOUCHER_LIST_RETRY',
                thai ? 'ลองอีกครั้ง' : 'Retry',
              )}
            </button>
          </div>
        ) : (
          <VoucherList
            isLoading={isLoading}
            isCheckout={isCheckout}
            isCoachMarkPresent={isCoachMarkPresent}
            isFlagForAllUserValid={isFlagForAllUserValid}
            isOpenCoachMark={isOpenCoachMark}
            voucherPieceRef={voucherPieceRef}
            voucherList={voucherList}
            appliedVoucher={appliedVoucher}
            onVoucherSelected={onVoucherSelected}
            onCopyVoucherCode={handleCopyVoucherCode}
            onSaveCoachMarkClosed={handleSaveCoachMarkClosed}
            isApplying={isApplying}
            pendingVoucher={pendingVoucher}
            pendingLabel={pendingLabel}
          />
        )}
      </div>
      <div className="pdp__voucher-modal__snack-bar">
        <SnackBar
          open={isSnackBarOpen}
          duration={10000}
          onClose={() => setIsSnackBarOpen(false)}
        >
          <span className={clsx('text', Typo.caption)}>
            {hasLocizeTranslation(
              t,
              'VOUCHER_COPIED',
              'Code copied, please apply in Shopping Bag!',
            )}
          </span>
        </SnackBar>
      </div>
    </div>
  )
}

VoucherListModal.defaultProps = {
  isLoading: false,
  appliedVoucher: '',
  productId: '',
  t: undefined,
  onVoucherSelected: undefined,
  isApplying: false,
  pendingVoucher: '',
  applicationError: null,
  listError: false,
  onRetry: undefined,
  language: '',
  onCodeEntered: undefined,
}

VoucherListModal.propTypes = {
  isLoading: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  isCheckout: PropTypes.bool.isRequired,
  appliedVoucher: PropTypes.string,
  voucherList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func,
  onVoucherSelected: PropTypes.func,
  isApplying: PropTypes.bool,
  pendingVoucher: PropTypes.string,
  applicationError: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ message: PropTypes.string }),
    ]),
  }),
  listError: PropTypes.bool,
  onRetry: PropTypes.func,
  language: PropTypes.string,
  onCodeEntered: PropTypes.func,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
  })),
  withI18next(),
)(VoucherListModal)
